/*
   This file defines a React component responsible for building a parts
   catalog. It supports the existing Bill of Materials (BOM) upload and
   introduces a separate translation upload for figure titles and part
   descriptions in multiple languages. The translation upload does not
   modify the BOM format; instead, it allows authors to supply optional
   language‑specific text alongside the existing catalog data. During
   saving, the component assembles a separate translation payload which
   can be sent to a new backend endpoint without disrupting the core
   catalog save flow.
*/

import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import * as XLSX from "xlsx";
import { fetchMastersWithStatus } from "../../apiServices/partsCatalogStatusApi";
import { masterAssemblyApi } from "../../apiServices/masterassembly";
import { useAuthoringStore } from "../../store/UseAuthorStore";
import { useUserStore } from "../../store/userStore";
// Keep existing types from models. We'll extend them locally below.
import type {
  BomItem,
  CatalogFigure,
  MasterAssembly,
  SvgFile,
} from "../../models/MasterAssembly";
import "./CatalogBuilder.css";

/**
 * Translation interfaces
 *
 * Authors can upload a separate translation spreadsheet for each figure. The
 * spreadsheet may contain a row labelled "FigureTitle" to supply the
 * translated figure title in multiple languages, followed by rows of
 * part number translations. Each language column is indicated by a
 * header starting with "Description". The parsing logic converts the
 * spreadsheet into the following structures.
 */
export interface LocalizedTranslation {
  /** Human‑readable language identifier (e.g. "English", "Spanish"). */
  language: string;
  /** The translated text corresponding to the given language. */
  value: string;
}

export interface PartTranslation {
  /** The part number this translation belongs to. */
  partNumber: string;
  /** All language translations for this part. */
  translations: LocalizedTranslation[];
}

export interface FigureTranslationData {
  /** Optional translations for the figure title in multiple languages. */
  figureTitleTranslations: LocalizedTranslation[];
  /** Optional translations for part descriptions per language. */
  partTranslations: PartTranslation[];
  /** The file name of the uploaded translation document. */
  fileName?: string;
}

/**
 * Payload used when saving translations. Each figure's numeric figure
 * identifier (without the "Fig" prefix) is sent along with its
 * translations. This payload is assembled when the author saves the
 * catalog and passed to a new backend endpoint.
 */
export interface FigureTranslationPayload {
  figNumber: string;
  figTitleTranslations: LocalizedTranslation[];
  partDescriptionTranslations: PartTranslation[];
}

export interface CatalogTranslationPayload {
  masterAssemblyId: number;
  oem: string;
  figures: FigureTranslationPayload[];
}

const CatalogBuilder: React.FC = () => {
  const { masterAssemblyId } = useParams();
  const navigate = useNavigate();
  const user = useUserStore((s) => s.user);
  const role = String(user?.role || "OEM").toUpperCase();

  const basePath = process.env.REACT_APP_BASE_NAME || "";
  const oemFromBase = basePath.split("/")[1] || "";
  const oemFromUser = user?.oemId ? `Oem${user.oemId}` : "";
  const oem = oemFromBase || oemFromUser || "Oem1";

  const {
    setMasterAssembly,
    setPartNumber,
    setPartDescription,
    setPartRevision,
    setOEMName,
    addCard,
    deleteCard,
    pushIllustrations,
    buildAddPayload,
    cards,
    masterAssembly,
  } = useAuthoringStore();

  const buildMediaUrl = (path: string) => {
    const p = String(path || "").trim();
    if (!p) return "";
    if (/^https?:\/\//i.test(p)) return p;
    const baseUrl = process.env.REACT_APP_API_BASE_URL || "";
    if (p.startsWith("/media/")) return `${baseUrl}${p}`;
    if (p.startsWith("media/")) return `${baseUrl}/${p}`;
    return `${baseUrl}/media/${p}`;
  };

  const normalizeFigNumber = (value: string) => {
    const v = String(value || "").trim();
    if (!v) return "";
    const raw = v.toLowerCase().startsWith("fig") ? v.slice(3).trim() : v;
    return `Fig ${raw}`;
  };

  const getNumericFig = (value: string) => {
    const v = String(value || "").trim();
    if (!v) return "";
    const raw = v.toLowerCase().startsWith("fig") ? v.slice(3).trim() : v;
    return raw;
  };

  const isValidFigNumber = (value: string) => /^\d+(\.\d+)?$/.test(getNumericFig(value));
  const figureNumberKey = (value: string) => getNumericFig(value).trim().toLowerCase();
  const isFigureTitleFilled = (figure: CatalogFigure) => String(figure.figTitle || "").trim().length > 0;
  const isFigureNumberUnique = (figureIndex: number, list: CatalogFigure[]) => {
    const key = figureNumberKey(String(list[figureIndex]?.figNumber || ""));
    if (!key) return false;
    return list.every((f, idx) => idx === figureIndex || figureNumberKey(String(f.figNumber || "")) !== key);
  };
  const getFigureValidationErrors = (figure: CatalogFigure, figureIndex: number, list: CatalogFigure[]) => {
    const errors: string[] = [];
    const figNo = String(figure.figNumber || "").trim();
    if (!figNo || !isValidFigNumber(figNo)) {
      errors.push('Figure number is required in format "Fig 1" or "Fig 1.1".');
    } else if (!isFigureNumberUnique(figureIndex, list)) {
      errors.push("Figure number must be unique across all figures.");
    }
    if (!isFigureTitleFilled(figure)) {
      errors.push("Figure title is required.");
    }
    if (!figure.svgFiles || figure.svgFiles.length === 0) {
      errors.push("Illustration SVG is required.");
    }
    if (
      (!figure.bomFiles || figure.bomFiles.length === 0) &&
      (!figure.bomJson || figure.bomJson.length === 0)
    ) {
      errors.push("BOM is required.");
    }
    return errors;
  };

  const [loading, setLoading] = useState(false);
  const [validatingAll, setValidatingAll] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      setError(null);
      setMessage(null);
      try {
        const list = await fetchMastersWithStatus(oem, role);
        const targetId = Number(masterAssemblyId);
        const item = list.items.find((i) => i.masterAssemblyId === targetId);
        if (!item?.componentPartNumber) {
          throw new Error("Component not found");
        }
        const data: MasterAssembly = await masterAssemblyApi.getByOEMAndPartNumber(oem, item.componentPartNumber);
        if (!mounted) return;
        setMasterAssembly(data);
        setOEMName(oem);
        setPartNumber(data.componentPartNumber || "");
        setPartDescription((data.componentDescription || "").toUpperCase());
        if (data.currentVersionId != null) setPartRevision(String(data.currentVersionId));

        const normalizedCatalog = (data.catalog || []).map((fig: CatalogFigure) => ({
          ...fig,
          bomFiles: fig.bomFiles && fig.bomFiles.length
            ? fig.bomFiles
            : fig.bomJson && fig.bomJson.length
            ? [{ fileName: "Imported Catalog", items: fig.bomJson }]
            : [],
        }));
        useAuthoringStore.setState({ cards: normalizedCatalog });
      } catch (e: any) {
        if (mounted) setError(e?.message || "Failed to load component");
      } finally {
        if (mounted) setLoading(false);
      }
    };
    if (masterAssemblyId) void load();
    return () => {
      mounted = false;
    };
  }, [masterAssemblyId, oem, role, setMasterAssembly, setPartNumber, setPartDescription, setPartRevision]);

  /**
   * Parses an uploaded Excel file into a list of BOM items.
   *
   * The Excel sheet is expected to have a header row with columns such as
   * "Item Number", "Part Number", "Description", "Qty", "UOM", "Price",
   * and optional "Callout". The parser is tolerant of variations in
   * header names (e.g. "Part No", "ItemNo", etc.). Only rows where both
   * part number and description are present will be returned.
   */
  const parseExcelToBomItems = (file: File): Promise<BomItem[]> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const wb = XLSX.read(data, { type: "array" });
          const sheet = wb.Sheets[wb.SheetNames[0]];
          const rows: any[] = XLSX.utils.sheet_to_json(sheet, { defval: "" });
          // Normalize header names by removing spaces, underscores and dots and lowercasing.
          const normalize = (s: string) => s.toLowerCase().replace(/[\s._]/g, "");
          const items: BomItem[] = rows.map((r: any) => {
            const keys = Object.keys(r);
            // Helper to find cell value by matching column names.
            const getVal = (cands: string[], fallback: any = "") => {
              for (const k of keys) {
                if (cands.some((c) => normalize(k).includes(c))) return r[k];
              }
              return fallback;
            };
            return {
              itemNumber: Number(getVal(["item", "itemno", "itemnumber", "srno"], 0)),
              partNumber: String(getVal(["partnumber", "partno", "pn"], "")).trim(),
              description: String(getVal(["description", "desc"], "")).trim(),
              qty: Number(getVal(["qty", "quantity", "qtyreq"], 0)),
              uom: String(getVal(["uom", "unit", "unitofmeasure"], "")).trim(),
              price: Number(getVal(["price", "amount", "cost", "rate"], 0)) || null,
              calloutId: String(getVal(["callout", "balloon", "refdes"], "")).trim(),
              partId: null,
              figId: null,
            };
          });
          // Require both Part Number and Description for each BOM row.
          resolve(items.filter((i) => i.partNumber && i.description));
        } catch (err) {
          reject(err);
        }
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });

  /**
   * Parses an uploaded Excel file into translation data for a figure.
   *
   * The first row of the sheet is expected to contain column headers. One of
   * the columns must be "PartNumber" (or a variation), and zero or more
   * columns must begin with "Description" to indicate languages. If a
   * subsequent row has the value "FigureTitle" in the PartNumber column,
   * the cells in each language column represent the translated figure title
   * for that language. All other rows correspond to part description
   * translations and must specify a part number. Only rows with at least
   * one non‑empty translation value are returned.
   */
  const parseTranslationExcel = (
    file: File
  ): Promise<{ figureTitleTranslations: LocalizedTranslation[]; partTranslations: PartTranslation[] }> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const wb = XLSX.read(data, { type: "array" });
          const sheet = wb.Sheets[wb.SheetNames[0]];
          // Read as row arrays (header: 1). defval ensures empty strings for empty cells.
          const rawRows: any[][] = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "" });
          if (!rawRows.length) {
            resolve({ figureTitleTranslations: [], partTranslations: [] });
            return;
          }
          const headerRow: any[] = rawRows[0] || [];
          // Determine indices
          let partNumberIndex: number | undefined;
          const languageCols: Array<{ index: number; language: string }> = [];
          const normalize = (s: string) => String(s || "").toLowerCase().replace(/[\s._]/g, "");
          const normalizeLanguageName = (value: string) => {
            const language = String(value || "").trim();
            const languageKey = language.toLowerCase();
            if (languageKey === "italy") return "Italian";
            return language || "English";
          };
          headerRow.forEach((headerValue, idx) => {
            const colName = String(headerValue || "").trim();
            const colNorm = normalize(colName);
            if (partNumberIndex === undefined && /(partnumber|partno|pn)/.test(colNorm)) {
              partNumberIndex = idx;
            } else if (/^description/.test(colNorm)) {
              const languagePart = colName.substring("Description".length).trim();
              const languageName = normalizeLanguageName(languagePart);
              languageCols.push({ index: idx, language: languageName });
            }
          });
          if (partNumberIndex === undefined || languageCols.length === 0) {
            // Invalid translation file: no part number or no language columns
            resolve({ figureTitleTranslations: [], partTranslations: [] });
            return;
          }
          const figureTitleTranslations: LocalizedTranslation[] = [];
          const partTranslations: PartTranslation[] = [];
          // Process each row after the header
          for (let rowIdx = 1; rowIdx < rawRows.length; rowIdx += 1) {
            const row = rawRows[rowIdx] || [];
            const pn = partNumberIndex !== undefined ? String(row[partNumberIndex] || "").trim() : "";
            if (!pn) continue;
            if (pn.toLowerCase() === "figuretitle") {
              // Figure title row
              languageCols.forEach((col) => {
                const val = String(row[col.index] || "").trim();
                figureTitleTranslations.push({ language: col.language, value: val });
              });
            } else {
              // Part translation row
              const translations: LocalizedTranslation[] = [];
              languageCols.forEach((col) => {
                const val = String(row[col.index] || "").trim();
                translations.push({ language: col.language, value: val });
              });
              const hasNonEmpty = translations.some((t) => t.value);
              if (hasNonEmpty) {
                partTranslations.push({ partNumber: pn, translations });
              }
            }
          }
          resolve({ figureTitleTranslations, partTranslations });
        } catch (err) {
          reject(err);
        }
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });

  /**
   * Handles translation Excel upload for a given figure index. Parses the
   * translation file and stores the resulting translation data and file
   * name on the figure object. This function does not perform any
   * validation beyond ensuring that at least one translation exists.
   */
  const handleTranslationFile = async (idx: number, file: File) => {
    try {
      const { figureTitleTranslations, partTranslations } = await parseTranslationExcel(file);
      if (
        (!figureTitleTranslations || figureTitleTranslations.every((t) => !t.value)) &&
        (!partTranslations || partTranslations.length === 0)
      ) {
        setError("No translations found in file.");
        return;
      }
      const updated = [...useAuthoringStore.getState().cards];
      // Store the parsed translations and file name on the figure
      (updated[idx] as any).translationData = {
        figureTitleTranslations,
        partTranslations,
        fileName: file.name,
      };
      // If the translation contains a figure title, update figTitle on the card.
      if (figureTitleTranslations && figureTitleTranslations.length) {
        const englishTitle = figureTitleTranslations.find((t) => t.language.toLowerCase() === "english");
        if (englishTitle?.value) {
          updated[idx].figTitle = englishTitle.value.toUpperCase();
        }
      }
      // Reset validation status to encourage revalidation if BOM changed
      updated[idx].validationStatus = undefined;
      useAuthoringStore.setState({ cards: updated });
    } catch {
      setError("Invalid translation file");
    }
  };

  const handleCatalogFile = async (idx: number, file: File) => {
    try {
      const items = await parseExcelToBomItems(file);
      if (!items.length) {
        setError("No valid BOM rows. Part Number and Description are required.");
        return;
      }
      const newBom = { fileName: file.name, items };
      const updated = [...useAuthoringStore.getState().cards];
      // Store the parsed BOM
      updated[idx].bomFiles = [newBom];
      // Reset the validation status
      updated[idx].validationStatus = undefined;
      // Clear any previously stored translation or figure title data
      delete (updated[idx] as any).localizedFigTitles;
      useAuthoringStore.setState({ cards: updated });
    } catch (err) {
      setError("Invalid BOM file");
    }
  };

  const uploadSvgsThenStore = async (idx: number, files: File[]) => {
    const { masterAssembly: master, cards: currentCards } = useAuthoringStore.getState();
    const masterAssemblyIdVal = master?.masterAssemblyId ?? 0;
    const figNumber = currentCards[idx]?.figNumber || `Fig ${idx + 1}`;
    if (!isValidFigNumber(figNumber)) {
      setError('Figure number must be "Fig 1" or "Fig 1.1"');
      return;
    }
    const numericFig = getNumericFig(figNumber);
    const uploads: SvgFile[] = [];
    for (const file of files) {
      try {
        const meta = await (masterAssemblyApi as any)?.uploadSvg?.({
          file,
          masterAssemblyId: masterAssemblyIdVal,
          figNumber: numericFig,
          oem,
        });
        if (meta && typeof meta.svgId === "number") {
          uploads.push(meta as SvgFile);
        }
      } catch {
        // ignore upload errors per file
      }
    }
    if (uploads.length) pushIllustrations(idx, uploads);
  };

  /**
   * Normalize BOM items prior to validation or saving.
   *
   * This function sanitizes numeric values (removing commas and spaces),
   * trims strings and ensures empty numeric fields default to zero. It
   * preserves the original `description` property without any language
   * semantics.
   */
  const normalizeBomItem = (i: BomItem): BomItem => {
    const sanitizeNumber = (val: any): number => {
      if (val === null || val === undefined || val === "") return 0;
      const n = Number(String(val).replace(/[, ]/g, ""));
      return isNaN(n) ? 0 : n;
    };
    const sanitizeString = (val: any): string => String(val ?? "").trim();
    return {
      itemNumber: sanitizeNumber(i.itemNumber),
      partNumber: sanitizeString(i.partNumber),
      description: sanitizeString(i.description),
      qty: sanitizeNumber(i.qty),
      uom: sanitizeString(i.uom),
      price:
        i.price === null || i.price === undefined || isNaN(Number(i.price))
          ? null
          : Number(String(i.price).replace(/[, ]/g, "")),
      calloutId: sanitizeString(i.calloutId),
      partId: i.partId ?? null,
      figId: i.figId ?? null,
    };
  };

  const getBomForValidation = (figure: CatalogFigure) => {
    let sourceBom: BomItem[] = [];
    if (figure.bomFiles && figure.bomFiles.length > 0) {
      sourceBom = figure.bomFiles.flatMap((f: any) => f.items || []);
    } else if (figure.bomJson && figure.bomJson.length > 0) {
      sourceBom = figure.bomJson as BomItem[];
    }
    return sourceBom.map((i) => normalizeBomItem(i)).filter((i) => i.partNumber && i.description);
  };

  const handleValidate = async (figureIndex: number) => {
    try {
      const { cards: currentCards } = useAuthoringStore.getState();
      const figure = currentCards[figureIndex];
      const figureValidationErrors = getFigureValidationErrors(figure, figureIndex, currentCards);
      if (figureValidationErrors.length > 0) {
        const updated = [...currentCards];
        updated[figureIndex].validationStatus = "error";
        useAuthoringStore.setState({ cards: updated });
        setError(figureValidationErrors[0]);
        return;
      }
      const bomForValidation = getBomForValidation(figure);
      if (!bomForValidation.length) {
        const updated = [...currentCards];
        updated[figureIndex].validationStatus = "error";
        useAuthoringStore.setState({ cards: updated });
        setError("No valid BOM rows");
        return;
      }
      const response = await masterAssemblyApi.validateFigureBom(oem, {
        ...figure,
        figNumber: getNumericFig(figure.figNumber || ""),
        bomJson: bomForValidation,
      });
      const updated = [...currentCards];
      updated[figureIndex].validationStatus = response.statusCode === 200 ? "success" : "error";
      useAuthoringStore.setState({ cards: updated });
    } catch {
      const updated = [...useAuthoringStore.getState().cards];
      updated[figureIndex].validationStatus = "error";
      useAuthoringStore.setState({ cards: updated });
      setError("Validation failed");
    }
  };

  const handleValidateAllBoms = async () => {
    setValidatingAll(true);
    setError(null);
    setMessage(null);
    const summaryErrors: string[] = [];

    try {
      const { cards: currentCards } = useAuthoringStore.getState();
      if (!currentCards.length) {
        setError("No figures available to validate.");
        return;
      }

      const updated = [...currentCards];

      for (let figureIndex = 0; figureIndex < currentCards.length; figureIndex += 1) {
        const figure = currentCards[figureIndex];
        const figureLabel = `Figure ${figureIndex + 1}`;
        const figureValidationErrors = getFigureValidationErrors(figure, figureIndex, currentCards);

        if (figureValidationErrors.length > 0) {
          updated[figureIndex].validationStatus = "error";
          summaryErrors.push(`${figureLabel}: ${figureValidationErrors.join(" ")}`);
          useAuthoringStore.setState({ cards: [...updated] });
          continue;
        }

        const bomForValidation = getBomForValidation(figure);
        if (!bomForValidation.length) {
          updated[figureIndex].validationStatus = "error";
          summaryErrors.push(`${figureLabel}: No valid BOM rows.`);
          useAuthoringStore.setState({ cards: [...updated] });
          continue;
        }

        try {
          const response = await masterAssemblyApi.validateFigureBom(oem, {
            ...figure,
            figNumber: getNumericFig(figure.figNumber || ""),
            bomJson: bomForValidation,
          });
          const isSuccess = response?.statusCode === 200;
          updated[figureIndex].validationStatus = isSuccess ? "success" : "error";
          if (!isSuccess) {
            summaryErrors.push(`${figureLabel}: BOM validation failed.`);
          }
        } catch {
          updated[figureIndex].validationStatus = "error";
          summaryErrors.push(`${figureLabel}: BOM validation failed.`);
        }

        useAuthoringStore.setState({ cards: [...updated] });
      }

      if (summaryErrors.length > 0) {
        setError(`Validate BOMs failed. ${summaryErrors.join(" ")}`);
      } else {
        setMessage("All BOMs validated successfully.");
      }
    } finally {
      setValidatingAll(false);
    }
  };

  const handleSave = async () => {
    if (!masterAssembly?.masterAssemblyId) {
      setError("Missing master assembly.");
      return;
    }
    setLoading(true);
    setError(null);
    setMessage(null);
    try {
      // Build the core catalog payload and save it first.
      const catalogPayload = buildAddPayload();
      catalogPayload.masterAssemblyId = masterAssembly.masterAssemblyId;
      const resp = await masterAssemblyApi.updateMaster(oem, catalogPayload);
      if (resp?.statusCode === 200) {
        // Prepare translation payload by collecting data from each figure.
        const translationPayload: CatalogTranslationPayload = {
          masterAssemblyId: masterAssembly.masterAssemblyId,
          oem,
          figures: [],
        };
        const isBackendTranslation = (translation: LocalizedTranslation) => {
          const language = String(translation.language || "").trim().toLowerCase();
          return Boolean(translation.value?.trim()) && language !== "english" && language !== "en";
        };
        cards.forEach((figure) => {
          const data = (figure as any).translationData as FigureTranslationData | undefined;
          // Use the numeric part of the figure number (e.g. "1" for "Fig 1").
          const figNumber = getNumericFig(figure.figNumber || "");
          if (data && figNumber) {
            const figTitleTranslations = (data.figureTitleTranslations || []).filter(isBackendTranslation);
            const partDescriptionTranslations = (data.partTranslations || [])
              .map((partTranslation) => ({
                ...partTranslation,
                translations: (partTranslation.translations || []).filter(isBackendTranslation),
              }))
              .filter((partTranslation) => partTranslation.translations.length > 0);
            const hasTitle = figTitleTranslations.length > 0;
            const hasParts = partDescriptionTranslations.length > 0;
            if (hasTitle || hasParts) {
              translationPayload.figures.push({
                figNumber,
                figTitleTranslations,
                partDescriptionTranslations,
              });
            }
          }
        });
        // If there are translations to save, call the new backend method.
        if (translationPayload.figures.length > 0) {
          try {
            const tResp = await (masterAssemblyApi as any)?.updateMasterTranslations?.(oem, translationPayload);
            if (tResp && tResp.statusCode === 200) {
              setMessage("✅ Catalog and translations saved");
            } else {
              // Catalog saved, but translation update may have failed.
              setMessage("✅ Catalog saved, but translation update failed");
            }
          } catch {
            setMessage("✅ Catalog saved, but translation update failed");
          }
        } else {
          // No translation data; indicate success.
          setMessage("✅ Catalog saved");
        }
      } else {
        throw new Error(resp?.message || "Save failed");
      }
    } catch (e: any) {
      setError(e?.message || "Save failed");
    } finally {
      setLoading(false);
    }
  };

  const moveFigure = (fromIndex: number, toIndex: number) => {
    const current = [...useAuthoringStore.getState().cards];
    if (toIndex < 0 || toIndex >= current.length) return;

    const [moved] = current.splice(fromIndex, 1);
    current.splice(toIndex, 0, moved);

    const reordered = current.map((figure, i) => ({
      ...figure,
      figOrder: i + 1,
    }));

    useAuthoringStore.setState({ cards: reordered });
  };

  const allValidateBomSuccess =
    cards.length > 0 &&
    cards.every(
      (figure, idx) =>
        figure.validationStatus === "success" &&
        getFigureValidationErrors(figure, idx, cards).length === 0
    );

  return (
    <div className="catalog-builder">
      <div className="catalog-builder__shell">
        <div className="catalog-builder__topbar">
          <div className="catalog-builder__title-wrap">
            <h2>Catalog Builder</h2>
            <div className="catalog-builder__meta">
              <div className="catalog-builder__meta-item">
                <span className="catalog-builder__meta-label">Part Number</span>
                <span className="catalog-builder__meta-value">
                  {masterAssembly?.componentPartNumber || "-"}
                </span>
              </div>
              <div className="catalog-builder__meta-item">
                <span className="catalog-builder__meta-label">Description</span>
                <span className="catalog-builder__meta-value">
                  {(masterAssembly?.componentDescription || "").toUpperCase() || "-"}
                </span>
              </div>
            </div>
          </div>

          <div className="catalog-builder__topbar-actions">
            <button className="btn-outline catalog-builder__btn" onClick={() => navigate(-1)}>
              Back
            </button>
            <button
              className="btn-primary catalog-builder__btn"
              onClick={handleSave}
              disabled={loading || !allValidateBomSuccess}
            >
              {loading ? "Saving..." : "Save Catalog"}
            </button>
          </div>
        </div>

        <div className="catalog-builder__toolbar">
          <div className="catalog-builder__toolbar-actions">
            <button className="btn-primary catalog-builder__btn" onClick={() => addCard()}>
              Add Figure
            </button>
            <button
              className="btn-primary catalog-builder__btn"
              onClick={handleValidateAllBoms}
              disabled={loading || validatingAll}
            >
              {validatingAll ? "Validating BOMs..." : "Validate BOMs"}
            </button>
          </div>
          <div className="catalog-builder__figure-count">Figures: {cards.length}</div>
        </div>

        {error && <div className="catalog-builder__message error">{error}</div>}
        {message && <div className="catalog-builder__message success">{message}</div>}
        {!allValidateBomSuccess && (
          <div className="catalog-builder__message warn">
            Validate BOM for all figures successfully before saving catalog.
          </div>
        )}

        <div className="catalog-builder__list">
          {cards.map((fig, idx) => {
            const validationStatus = fig.validationStatus || "pending";
            const isFigureNoValid = isValidFigNumber(fig.figNumber || "");
            const figureValidationErrors = getFigureValidationErrors(fig, idx, cards);

            return (
              <section
                key={`${fig.figId}-${idx}`}
                className={`catalog-builder__figure is-${validationStatus}`}
              >
                <div className="catalog-builder__figure-head">
                  <div className="catalog-builder__figure-head-left">
                    <span className="catalog-builder__figure-kicker">Figure {idx + 1}</span>
                    <div className="catalog-builder__figure-inputs">
                      <div className="catalog-builder__figure-input">
                        <label>Figure No</label>
                        <input
                          value={fig.figNumber || `Fig ${idx + 1}`}
                          onChange={(e) => {
                            const updated = [...useAuthoringStore.getState().cards];
                            updated[idx].figNumber = normalizeFigNumber(e.target.value);
                            updated[idx].validationStatus = undefined;
                            useAuthoringStore.setState({ cards: updated });
                          }}
                          placeholder="Fig 1"
                        />
                      </div>
                      <div className="catalog-builder__figure-input catalog-builder__figure-input--wide">
                        <label>Figure Title</label>
                        <input
                          value={fig.figTitle || ""}
                          onChange={(e) => {
                            const updated = [...useAuthoringStore.getState().cards];
                            updated[idx].figTitle = e.target.value.toUpperCase();
                            updated[idx].validationStatus = undefined;
                            useAuthoringStore.setState({ cards: updated });
                          }}
                          placeholder="FIGURE TITLE"
                        />
                      </div>
                    </div>
                    {!isFigureNoValid && <div className="catalog-builder__fig-error">Figure number format is invalid.</div>}
                  </div>

                  <div className="catalog-builder__figure-head-right">
                    <span className={`catalog-builder__status-pill ${validationStatus}`}>
                      {validationStatus.toUpperCase()}
                    </span>
                    <button
                      className="btn-outline catalog-builder__btn catalog-builder__btn-move"
                      onClick={() => moveFigure(idx, idx - 1)}
                      disabled={idx === 0}
                    >
                      Move Up
                    </button>
                    <button
                      className="btn-outline catalog-builder__btn catalog-builder__btn-move"
                      onClick={() => moveFigure(idx, idx + 1)}
                      disabled={idx === cards.length - 1}
                    >
                      Move Down
                    </button>
                    <button
                      className="btn-outline catalog-builder__btn catalog-builder__btn-danger"
                      onClick={() => deleteCard(idx)}
                    >
                      Delete Figure
                    </button>
                  </div>
                </div>

                <div className="catalog-builder__figure-layout">
                  <div className="catalog-builder__section">
                    <div className="catalog-builder__section-head">
                      <h3>Illustration (SVG)</h3>
                      <input
                        type="file"
                        accept=".svg"
                        multiple
                        onChange={(e) => {
                          const filesArr = Array.from(e.target.files || []);
                          if (filesArr.length) void uploadSvgsThenStore(idx, filesArr);
                          e.currentTarget.value = "";
                        }}
                      />
                    </div>
                    <div className="catalog-builder__section-body">
                      {fig.svgFiles && fig.svgFiles.length ? (
                        <div className="catalog-builder__thumbs">
                          {fig.svgFiles.map((svg: SvgFile, i: number) => (
                            <div key={`${svg.svgId}-${svg.fileName}-${i}`} className="catalog-builder__thumb">
                              {buildMediaUrl(svg.folderPath) ? (
                                <img src={buildMediaUrl(svg.folderPath)} alt={svg.fileName} />
                              ) : null}
                              <span>{svg.fileName}</span>
                              <button
                                type="button"
                                className="catalog-builder__remove-btn"
                                onClick={() => {
                                  const updated = [...useAuthoringStore.getState().cards];
                                  updated[idx].svgFiles = (updated[idx].svgFiles || []).filter((_: any, j: number) => j !== i);
                                  updated[idx].validationStatus = undefined;
                                  useAuthoringStore.setState({ cards: updated });
                                }}
                              >
                                Remove
                              </button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="catalog-builder__empty-inline">No figures uploaded</div>
                      )}
                    </div>
                  </div>

                  <div className="catalog-builder__section">
                    <div className="catalog-builder__section-head">
                      <h3>Bill of Materials (Excel)</h3>
                      <input
                        type="file"
                        accept=".xls,.xlsx"
                        onChange={(e) => {
                          const fileObj = e.target.files?.[0];
                          if (fileObj) void handleCatalogFile(idx, fileObj);
                          e.currentTarget.value = "";
                        }}
                      />
                    </div>
                    <div className="catalog-builder__section-body">
                      {fig.bomFiles && fig.bomFiles.length ? (
                        <div className="catalog-builder__bom">
                          {fig.bomFiles.map((f: any, i: number) => (
                            <div key={`${f.fileName}-${i}`} className="catalog-builder__bom-item">
                              <span>{f.fileName}</span>
                              <button
                                type="button"
                                className="catalog-builder__remove-btn"
                                onClick={() => {
                                  const updated = [...useAuthoringStore.getState().cards];
                                  updated[idx].bomFiles = (updated[idx].bomFiles || []).filter((_: any, j: number) => j !== i);
                                  updated[idx].validationStatus = undefined;
                                  useAuthoringStore.setState({ cards: updated });
                                }}
                              >
                                Remove
                              </button>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="catalog-builder__empty-inline">No BOM uploaded</div>
                      )}
                    </div>
                  </div>

                  {/* Translation upload section */}
                  <div className="catalog-builder__section">
                    <div className="catalog-builder__section-head">
                      <h3>Translations (Excel)</h3>
                      <input
                        type="file"
                        accept=".xls,.xlsx"
                        onChange={(e) => {
                          const tfile = e.target.files?.[0];
                          if (tfile) void handleTranslationFile(idx, tfile);
                          e.currentTarget.value = "";
                        }}
                      />
                    </div>
                    <div className="catalog-builder__section-body">
                      {(fig as any).translationData && (fig as any).translationData.fileName ? (
                        <div className="catalog-builder__bom">
                          <div className="catalog-builder__bom-item">
                            <span>{(fig as any).translationData.fileName}</span>
                            <button
                              type="button"
                              className="catalog-builder__remove-btn"
                              onClick={() => {
                                const updated = [...useAuthoringStore.getState().cards];
                                delete (updated[idx] as any).translationData;
                                updated[idx].validationStatus = undefined;
                                useAuthoringStore.setState({ cards: updated });
                              }}
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="catalog-builder__empty-inline">No translation uploaded</div>
                      )}
                    </div>
                  </div>

                  <div className="catalog-builder__validation">
                    <div className="catalog-builder__validation-head">
                      <h3>Validation</h3>
                      <span className={`catalog-builder__status-pill ${validationStatus}`}>
                        {validationStatus.toUpperCase()}
                      </span>
                    </div>
                    <p className="catalog-builder__validation-note">
                      Ensure BOM item numbers match illustration callouts before saving.
                    </p>
                    <button className="btn-primary catalog-builder__btn" onClick={() => handleValidate(idx)}>
                      Validate BOM
                    </button>
                    {figureValidationErrors.map((msg, i) => (
                      <div key={`${idx}-validation-error-${i}`} className="catalog-builder__fig-error">
                        {msg}
                      </div>
                    ))}
                  </div>
                </div>
              </section>
            );
          })}
          {cards.length === 0 && <div className="catalog-builder__empty">No figures yet.</div>}
        </div>
      </div>
    </div>
  );
};

export default CatalogBuilder;
